import { styled } from '@mui/material/styles';
import classNames from 'clsx';
// eslint-disable-next-line aveny/mui-import-rule
import Grid from '@mui/material/GridLegacy';
import AccessTime from '@mui/icons-material/AccessTime';
import Lens from '@mui/icons-material/Lens';
import { getAppointmentColor, getResourceColor } from '../utils';
import moment from 'moment';

const PREFIX = 'Content';
const HOUR_MINUTE_OPTIONS = { hour: 'numeric', minute: 'numeric' };
const WEEKDAY_INTERVAL = 'weekdayInterval';
const DAY_OPTIONS = { day: 'numeric' };
const SHORT_MONTH_OPTIONS = { month: 'short' };
const MONTH_YEAR_OPTIONS = { month: 'long', year: 'numeric' };
const DAY_SHORT_MONTH_OPTIONS = { day: 'numeric', month: 'short' };
const LONG_WEEK_DAY_OPTIONS = { weekday: 'long' };
const DAY_SHORT_MONTH_LONG_YEAR_OPTIONS = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
};
const EMPTY_OPTIONS = {};
const SHORT_MONTH_LONG_YEAR_OPTIONS = { month: 'short', year: 'numeric' };
const SHORT_MONTH_SHORT_YEAR_OPTIONS = { month: 'short', year: '2-digit' };
const DAY_SHORT_MONTH_SHORT_YEAR_OPTIONS = {
  day: 'numeric',
  month: 'short',
  year: '2-digit',
};
const DAY_LONG_MONTH_LONG_YEAR_OPTIONS = {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
};

let calculateTextByDays = function (
  startViewDate: moment.MomentInput,
  endViewDate: moment.MomentInput,
  formatDate: (arg0: Date, arg1: any) => string,
  additionalOptions: any
) {
  let momentStartViewDate = moment(startViewDate);
  let momentEndViewDate = moment(endViewDate);
  if (momentStartViewDate.isSame(momentEndViewDate, 'day')) {
    return formatDate(
      momentStartViewDate.toDate(),
      Object.assign(
        Object.assign({}, DAY_LONG_MONTH_LONG_YEAR_OPTIONS),
        additionalOptions
      )
    );
  }
  if (momentStartViewDate.isSame(momentEndViewDate, 'year')) {
    if (momentStartViewDate.isSame(momentEndViewDate, 'month')) {
      return (
        formatDate(momentStartViewDate.toDate(), DAY_OPTIONS) +
        '-' +
        formatDate(momentEndViewDate.toDate(), DAY_OPTIONS) +
        ' ' +
        formatDate(momentEndViewDate.toDate(), MONTH_YEAR_OPTIONS)
      );
    }
    return (
      formatDate(momentStartViewDate.toDate(), DAY_SHORT_MONTH_OPTIONS) +
      ' - ' +
      formatDate(momentEndViewDate.toDate(), DAY_SHORT_MONTH_LONG_YEAR_OPTIONS)
    );
  }
  return (
    formatDate(
      momentStartViewDate.toDate(),
      DAY_SHORT_MONTH_SHORT_YEAR_OPTIONS
    ) +
    ' - ' +
    formatDate(momentEndViewDate.toDate(), DAY_SHORT_MONTH_SHORT_YEAR_OPTIONS)
  );
};

let calculateTextByMonths = function (
  currentDate: moment.MomentInput,
  intervalCount: number,
  formatDate: (arg0: Date, arg1: { month: string; year?: string }) => string
) {
  let momentCurrentDate = moment(currentDate);
  if (intervalCount === 1) {
    return formatDate(momentCurrentDate.toDate(), MONTH_YEAR_OPTIONS);
  }
  let lastMonth = momentCurrentDate.clone().add(intervalCount - 1, 'month');
  if (momentCurrentDate.isSame(lastMonth, 'year')) {
    return (
      formatDate(momentCurrentDate.toDate(), SHORT_MONTH_OPTIONS) +
      '-' +
      formatDate(lastMonth.toDate(), SHORT_MONTH_LONG_YEAR_OPTIONS)
    );
  }
  return (
    formatDate(momentCurrentDate.toDate(), SHORT_MONTH_SHORT_YEAR_OPTIONS) +
    ' - ' +
    formatDate(lastMonth.toDate(), SHORT_MONTH_SHORT_YEAR_OPTIONS)
  );
};

let viewBoundText = function (
  startViewDate: any,
  endViewDate: any,
  type: string,
  currentDate: any,
  intervalCount: number,
  formatDate: any
) {
  return type !== 'month'
    ? calculateTextByDays(
        startViewDate,
        endViewDate,
        formatDate,
        type === WEEKDAY_INTERVAL ? LONG_WEEK_DAY_OPTIONS : EMPTY_OPTIONS
      )
    : calculateTextByMonths(currentDate, intervalCount, formatDate);
};

export const classes = {
  content: `${PREFIX}-content`,
  text: `${PREFIX}-text`,
  title: `${PREFIX}-title`,
  icon: `${PREFIX}-icon`,
  lens: `${PREFIX}-lens`,
  lensMini: `${PREFIX}-lensMini`,
  textCenter: `${PREFIX}-textCenter`,
  dateAndTitle: `${PREFIX}-dateAndTitle`,
  titleContainer: `${PREFIX}-titleContainer`,
  contentContainer: `${PREFIX}-contentContainer`,
  resourceContainer: `${PREFIX}-resourceContainer`,
  recurringIcon: `${PREFIX}-recurringIcon`,
  relativeContainer: `${PREFIX}-relativeContainer`,
};

const StyledDiv = styled('div')(
  // @ts-expect-error: 'resources' is not a valid prop type for styled div
  ({ theme: { spacing, palette, typography }, resources }) => ({
    [`&.${classes.content}`]: {
      padding: spacing(1.5, 1),
      paddingTop: spacing(1),
      backgroundColor: palette.background.paper,
      boxSizing: 'border-box',
      ...typography.body2,
    },
    [`& .${classes.text}`]: {
      display: 'inline-block',
    },
    [`& .${classes.title}`]: {
      ...typography.h6,
      color: palette.text.secondary,
      fontWeight: typography.fontWeightBold,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    [`& .${classes.icon}`]: {
      verticalAlign: 'middle',
      color: palette.action.active,
    },
    [`& .${classes.lens}`]: {
      color: getAppointmentColor(
        300,
        getResourceColor(resources),
        palette.primary
      ),
      width: spacing(4.5),
      height: spacing(4.5),
      verticalAlign: 'super',
      position: 'absolute',
      left: '50%',
      transform: 'translate(-50%,0)',
    },
    [`& .${classes.lensMini}`]: {
      width: spacing(2.5),
      height: spacing(2.5),
    },
    [`& .${classes.textCenter}`]: {
      textAlign: 'center',
      height: spacing(2.5),
    },
    [`& .${classes.dateAndTitle}`]: {
      lineHeight: 1.4,
    },
    [`& .${classes.titleContainer}`]: {
      paddingBottom: spacing(2),
    },
    [`& .${classes.contentContainer}`]: {
      paddingBottom: spacing(1.5),
    },
    [`& .${classes.resourceContainer}`]: {
      paddingBottom: spacing(0.25),
    },
    [`& .${classes.recurringIcon}`]: {
      position: 'absolute',
      paddingTop: spacing(0.875),
      left: '50%',
      transform: 'translate(-50%,0)',
      color: palette.background.paper,
      width: spacing(2.625),
      height: spacing(2.625),
    },
    [`& .${classes.relativeContainer}`]: {
      position: 'relative',
      width: '100%',
      height: '100%',
    },
  })
);

export const Content = ({
  // @ts-expect-error: Implicit any type for destructured props
  className,
  // @ts-expect-error: Implicit any type for destructured props
  children,
  // @ts-expect-error: Implicit any type for destructured props
  appointmentData,
  // @ts-expect-error: Implicit any type for destructured props
  appointmentResources,
  // @ts-expect-error: Implicit any type for destructured props
  formatDate,
  // @ts-expect-error: Implicit any type for destructured props
  recurringIconComponent: RecurringIcon,
  ...restProps
}) => {
  const weekDays = viewBoundText(
    appointmentData.startDate,
    appointmentData.endDate,
    WEEKDAY_INTERVAL,
    appointmentData.startDate,
    1,
    formatDate
  );
  return (
    <StyledDiv
      // @ts-expect-error: 'resources' is not a valid prop type for StyledDiv
      resources={appointmentResources}
      className={classNames(classes.content, className)}
      {...restProps}
    >
      <Grid
        container
        alignItems="flex-start"
        className={classes.titleContainer}
      >
        <Grid item xs={2}>
          <div className={classes.relativeContainer}>
            <Lens className={classes.lens} />
            {!!appointmentData.rRule && (
              <RecurringIcon className={classes.recurringIcon} />
            )}
          </div>
        </Grid>
        <Grid item xs={10}>
          <div>
            <div className={classNames(classes.title, classes.dateAndTitle)}>
              {appointmentData.title}
            </div>
            <div className={classNames(classes.text, classes.dateAndTitle)}>
              {weekDays}
            </div>
          </div>
        </Grid>
      </Grid>
      <Grid container alignItems="center" className={classes.contentContainer}>
        <Grid item xs={2} className={classes.textCenter}>
          <AccessTime className={classes.icon} data-testid="AccessTimeIcon" />
        </Grid>
        <Grid item xs={10}>
          <div className={classes.text}>
            {`${formatDate(appointmentData.startDate, HOUR_MINUTE_OPTIONS)}
              - ${formatDate(appointmentData.endDate, HOUR_MINUTE_OPTIONS)}`}
          </div>
        </Grid>
      </Grid>
      {/* @ts-expect-error: resourceItem implicitly has any type */}
      {appointmentResources.map(resourceItem => (
        <Grid
          container
          alignItems="center"
          className={classes.resourceContainer}
          key={`${resourceItem.fieldName}_${resourceItem.id}`}
        >
          <Grid item xs={2} className={classes.textCenter}>
            <div className={classes.relativeContainer}>
              <Lens
                className={classNames(classes.lens, classes.lensMini)}
                style={{ color: getAppointmentColor(300, resourceItem.color) }}
              />
            </div>
          </Grid>
          <Grid item xs={10}>
            <div className={classes.text}>{resourceItem.text}</div>
          </Grid>
        </Grid>
      ))}
      {children}
    </StyledDiv>
  );
};

Content.defaultProps = {
  appointmentData: undefined,
  appointmentResources: [],
  className: undefined,
  children: undefined,
};
